import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Ban, ExternalLink, LockKeyhole, LogOut, MessageSquare, RefreshCw, SearchX, ShieldAlert, Sparkles } from 'lucide-react'
import { Button, ButtonLink } from '../../components/ui/Button.jsx'
import { BrandMark } from '../../components/ui/BrandMark.jsx'
import { useAuth } from '../../features/auth/AuthContext.jsx'
import { getFormattedWhatsappNumber } from '../../data/demoStore.js'
import { useRealtimeSync } from '../../hooks/useRealtimeSync.js'
import { platformSettingsApi } from '../../services/plotfyApi.js'

function StatusPage({ icon: Icon, eyebrow, title, description, actionTo = '/', actionLabel = 'Kembali ke Beranda' }) {
  return (
    <main className="grid min-h-screen place-items-center bg-ink-950 px-4 py-10">
      <div className="w-full max-w-lg text-center">
        <div className="flex justify-center">
          <BrandMark />
        </div>
        <span className="mx-auto mt-12 grid size-14 place-items-center rounded-2xl border border-line bg-ink-900 text-brand-300">
          <Icon className="size-6" aria-hidden="true" />
        </span>
        <p className="mt-6 text-xs font-bold uppercase tracking-[.14em] text-brand-300">{eyebrow}</p>
        <h1 className="mt-3 text-3xl font-bold tracking-[-.04em] text-copy">{title}</h1>
        <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-copy-muted">{description}</p>
        <ButtonLink to={actionTo} className="mt-7">
          {actionLabel}
        </ButtonLink>
      </div>
    </main>
  )
}

export function NotFoundPage() {
  return <StatusPage icon={SearchX} eyebrow="404" title="Halaman tidak ditemukan" description="Alamat mungkin berubah atau halaman sudah tidak tersedia." />
}

export function UnauthorizedPage() {
  return <StatusPage icon={LockKeyhole} eyebrow="Akses terbatas" title="Ruang ini bukan untuk akunmu" description="Kembali ke dashboard yang sesuai dengan peran akun Plotfy kamu." actionTo="/" actionLabel="Kembali" />
}

export function SuspendedPage() {
  const { user, refreshUser, roleHome, logout } = useAuth()
  const navigate = useNavigate()
  const [checking, setChecking] = useState(false)
  const [waNumber, setWaNumber] = useState(() => getFormattedWhatsappNumber())

  useEffect(() => {
    platformSettingsApi
      .getPublicSettings()
      .then((res) => {
        if (res?.formattedWhatsappNumber) {
          setWaNumber(res.formattedWhatsappNumber)
        } else if (res?.adminWhatsappNumber) {
          setWaNumber(getFormattedWhatsappNumber(res.adminWhatsappNumber))
        }
      })
      .catch(() => {})
  }, [])
  const waMessage = encodeURIComponent(
    `Halo Admin Plotfy,\n\nAkun saya (${user?.email || user?.username || 'Pengguna'}) saat ini berstatus SUSPENDED.\nMohon bantuan dan klarifikasinya untuk proses pemulihan akun saya.\n\nTerima kasih.`
  )
  const waUrl = `https://wa.me/${waNumber}?text=${waMessage}`

  const checkStatus = useCallback(async () => {
    setChecking(true)
    try {
      const refreshed = await refreshUser()
      if (refreshed && refreshed.status === 'ACTIVE') {
        const destination = roleHome || (refreshed.role === 'BRAND' ? '/brand' : '/creator')
        navigate(destination, { replace: true })
      }
    } finally {
      setChecking(false)
    }
  }, [navigate, refreshUser, roleHome])

  // Automatically check status via realtime sync & every 4s polling
  useRealtimeSync(checkStatus, { intervalMs: 4000 })

  useEffect(() => {
    if (user && user.status === 'ACTIVE') {
      const destination = roleHome || (user.role === 'BRAND' ? '/brand' : '/creator')
      navigate(destination, { replace: true })
    }
  }, [navigate, roleHome, user])

  return (
    <main className="grid min-h-screen place-items-center bg-ink-950 px-4 py-10">
      <div className="w-full max-w-lg text-center space-y-6">
        <div className="flex justify-center">
          <BrandMark />
        </div>

        <div className="surface-card p-6 sm:p-8 border border-red-500/30 bg-ink-900/90 shadow-2xl space-y-5 rounded-2xl">
          <div className="mx-auto grid size-16 place-items-center rounded-2xl border border-red-500/30 bg-red-500/10 text-red-400">
            <ShieldAlert className="size-8 animate-pulse" aria-hidden="true" />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[.14em] text-red-400">Status: Akun Ditangguhkan</p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-white">Akun Anda Disuspend</h1>
            <p className="mx-auto mt-3 text-xs sm:text-sm leading-relaxed text-copy-muted">
              Seluruh akses menu, campaign, dan transaksi pada akun <strong>{user?.displayName || user?.email || 'Anda'}</strong> saat ini sedang dikunci oleh Administrator Plotfy.
            </p>
          </div>

          <div className="rounded-xl border border-line bg-ink-950 p-3.5 text-xs text-copy-secondary space-y-1">
            <p className="font-semibold text-copy">Akun Sudah Dibuka Kembali oleh Admin?</p>
            <p className="text-copy-muted text-[11px]">
              Jika Admin telah membuka suspend akun Anda, klik tombol di bawah untuk memuat ulang dan masuk ke dashboard.
            </p>
          </div>

          <div className="flex flex-col gap-2.5 pt-1">
            <button
              type="button"
              onClick={checkStatus}
              disabled={checking}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-xs sm:text-sm font-bold text-white shadow-lg hover:bg-brand-500 transition-colors disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={`size-4 ${checking ? 'animate-spin' : ''}`} />
              <span>{checking ? 'Memeriksa Status Akun...' : 'Periksa Status / Masuk ke Dashboard'}</span>
            </button>

            <div className="flex flex-col sm:flex-row gap-2.5">
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-xs sm:text-sm font-bold text-white shadow-lg hover:bg-emerald-500 transition-colors cursor-pointer"
              >
                <MessageSquare className="size-4" /> Hubungi Admin via WA
              </a>

              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-line bg-ink-950 px-4 py-2.5 text-xs font-semibold text-copy-muted hover:text-copy hover:bg-ink-850 transition-colors cursor-pointer"
              >
                <LogOut className="size-3.5" /> Keluar
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
